import { api } from '../client';
import type {
  Strategy,
  StrategiesListResponse,
  StrategyMetadata,
  StrategyInfo,
  SaveStrategyRequest,
  SaveStrategyResponse,
  PersistGraphLayoutRequest,
  PersistGraphLayoutResponse,
  CreateStrategyResponse,
  ToggleStrategyResponse,
  StrategyExportData,
  HeldTokensResponse,
  TokenTradesResponse,
  AlphaTradesResponse,
  EditorMetadata,
  TemplatesListResponse,
  TemplateStrategiesResponse,
  CreateFromTemplateResponse,
  TemplateEditPayload,
  UpdateTemplateStrategyResponse,
  BacktestTradesResponse,
  BacktestEventsResponse,
  TradingProfileResponse,
  PnLChartResponse,
  WalletTradesResponse,
  WalletTradeFilters,
  WalletInfoResponse,
  ActiveRecentStrategiesResponse,
  FifoSellBreakdown,
  BuyPerformanceResponse,
  ExportJobResponse,
  ExportStatusResponse,
  PerformanceTimeframe,
  PerformanceSummaryResponse,
  PerformanceRefresh,
  AdminStrategyPerformanceResponse,
} from '@/types/api/strategies';

type TemplateEditConcurrency = {
  runtimeRevision: number;
  variables: Array<{ name: string; lock_version: number }>;
};

/**
 * Strategies API endpoints
 */
export const strategiesAPI = {
  /**
   * Get list of strategies with pagination
   * @param page - Page number
   * @param perPage - Items per page
   * @param chainId - Optional chain ID filter
   */
  getStrategies: async (
    page: number = 1,
    perPage: number = 25,
    chainId?: number
  ): Promise<StrategiesListResponse> => {
    const params: Record<string, any> = { page, per_page: perPage };
    if (chainId) params.chain_id = chainId;
    return api.get<StrategiesListResponse>('/api/alpha_strategies.json', { params });
  },

  /**
   * Get strategy by ID
   * @param id - Strategy ID
   */
  getStrategy: async (id: number): Promise<Strategy> => {
    return api.get<Strategy>(`/api/alpha_strategies/${id}.json`);
  },

  /**
   * Get strategy metadata (includes graph)
   * @param id - Strategy ID
   */
  getStrategyMetadata: async (id: number): Promise<StrategyMetadata> => {
    return api.get<StrategyMetadata>(`/api/alpha_strategies/${id}/metadata`);
  },

  /**
   * Get strategy info (basic info without graph)
   * @param id - Strategy ID
   */
  getStrategyInfo: async (id: number): Promise<StrategyInfo> => {
    return api.get<StrategyInfo>(`/api/alpha_strategies/${id}/info`);
  },

  /**
   * Create a new strategy
   * @param chainId - Chain ID for the strategy
   */
  createStrategy: async (chainId?: number): Promise<CreateStrategyResponse> => {
    const payload = chainId ? { chain_id: chainId } : {};
    return api.post<CreateStrategyResponse>('/api/alpha_strategies', payload);
  },

  /**
   * Save strategy graph
   * @param id - Strategy ID
   * @param data - Strategy graph data
   */
  saveStrategy: async (id: number, data: SaveStrategyRequest): Promise<SaveStrategyResponse> => {
    return api.put<SaveStrategyResponse>(`/api/alpha_strategies/${id}`, data);
  },

  persistGraphLayout: async (
    id: number,
    data: PersistGraphLayoutRequest
  ): Promise<PersistGraphLayoutResponse> => {
    return api.patch<PersistGraphLayoutResponse>(`/api/alpha_strategies/${id}/graph_layout`, data);
  },

  /**
   * Rename strategy
   * @param id - Strategy ID
   * @param name - New name
   */
  renameStrategy: async (id: number, name: string): Promise<{ status: string }> => {
    return api.post<{ status: string }>(`/api/alpha_strategies/${id}/rename`, { name });
  },

  /**
   * Activate strategy
   * @param id - Strategy ID
   */
  activateStrategy: async (id: number): Promise<void> => {
    return api.post<void>(`/api/alpha_strategies/${id}/activate`, {});
  },

  /**
   * Deactivate strategy
   * @param id - Strategy ID
   */
  deactivateStrategy: async (id: number): Promise<void> => {
    return api.post<void>(`/api/alpha_strategies/${id}/deactivate`, {});
  },

  /**
   * Toggle strategy state (activate/deactivate)
   * @param id - Strategy ID
   */
  toggleStrategy: async (id: number): Promise<ToggleStrategyResponse> => {
    return api.post<ToggleStrategyResponse>(`/api/alpha_strategies/${id}/toggle`, {});
  },

  /**
   * Delete strategy
   * @param id - Strategy ID
   */
  deleteStrategy: async (id: number): Promise<{ status: string; message: string }> => {
    return api.delete<{ status: string; message: string }>(`/api/alpha_strategies/${id}`);
  },

  /**
   * Export strategy as JSON
   * @param id - Strategy ID
   */
  exportStrategy: async (id: number): Promise<StrategyExportData> => {
    return api.get<StrategyExportData>(`/api/alpha_strategies/${id}/export`);
  },

  /**
   * Import strategy from JSON
   * @param data - Strategy data to import
   */
  importStrategy: async (data: StrategyExportData): Promise<CreateStrategyResponse> => {
    return api.post<CreateStrategyResponse>('/api/alpha_strategies/import', { strategy: data });
  },

  /**
   * Get editor metadata (available nodes, methods, etc.)
   * @param chainId - Optional chain ID
   */
  getEditorMetadata: async (chainId?: number): Promise<EditorMetadata> => {
    const params = chainId ? { chain_id: chainId } : {};
    return api.get<EditorMetadata>('/api/alpha_strategies/editor_metadata', { params });
  },

  /**
   * Get held tokens for a strategy
   * @param id - Strategy ID
   * @param page - Page number
   * @param perPage - Items per page
   * @param sortBy - Sort field
   * @param sortDirection - Sort direction
   * @param filters - Optional filters
   */
  getHeldTokens: async (
    id: number,
    page: number = 1,
    perPage: number = 20,
    sortBy: string = 'latest_trade_date',
    sortDirection: 'asc' | 'desc' = 'desc',
    filters?: {
      exclude_low_liquidity?: boolean;
      token_address?: string;
    }
  ): Promise<HeldTokensResponse> => {
    const params: Record<string, any> = {
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };
    if (filters?.exclude_low_liquidity) params.exclude_low_liquidity = 'true';
    if (filters?.token_address) params.token_address = filters.token_address;

    return api.get<HeldTokensResponse>(`/api/alpha_strategies/${id}/held_tokens`, { params });
  },

  /**
   * Get token trades for a specific token in a strategy
   * @param id - Strategy ID
   * @param tokenAddress - Token contract address
   */
  getTokenTrades: async (id: number, tokenAddress: string): Promise<TokenTradesResponse> => {
    return api.get<TokenTradesResponse>(
      `/api/alpha_strategies/${id}/token_trades/${tokenAddress}`
    );
  },

  /**
   * Toggle excluded token
   * @param id - Strategy ID
   * @param tokenAddress - Token contract address
   */
  toggleExcludedToken: async (
    id: number,
    tokenAddress: string
  ): Promise<{ status: string; is_excluded: boolean }> => {
    return api.post<{ status: string; is_excluded: boolean }>(
      `/api/alpha_strategies/${id}/toggle_excluded_token`,
      { token_address: tokenAddress }
    );
  },

  /**
   * Get alpha trades for a strategy (paginated)
   */
  getAlphaTrades: async (
    id: number,
    page: number = 1,
    perPage: number = 25,
    filters?: {
      status?: string;
      action_type?: string;
    }
  ): Promise<AlphaTradesResponse> => {
    const params: Record<string, any> = { page, per_page: perPage };
    if (filters?.status) params.status = filters.status;
    if (filters?.action_type) params.action_type = filters.action_type;
    return api.get<AlphaTradesResponse>(`/api/alpha_strategies/${id}/alpha_trades`, { params });
  },

  /**
   * Refresh held tokens for a strategy
   */
  refreshHeldTokens: async (id: number): Promise<{ status: string; message: string }> => {
    return api.post<{ status: string; message: string }>(
      `/api/alpha_strategies/${id}/refresh_held_tokens`,
      {}
    );
  },

  /**
   * Get aggregated trading profile stats for the current user's wallets
   */
  getTradingProfile: async (chainId: number): Promise<TradingProfileResponse> => {
    return api.get<TradingProfileResponse>('/api/alpha_strategies/trading_profile', { params: { chain_id: chainId } });
  },

  /**
   * Get PnL chart data from on-chain sell trades (FIFO)
   * @param chainId - Chain numeric ID
   * @param period - Time period: 1d, 7d, 30d, or all
   */
  getPnLChart: async (chainId: number, period: string = '7d'): Promise<PnLChartResponse> => {
    return api.get<PnLChartResponse>('/api/alpha_strategies/pnl_chart', { params: { chain_id: chainId, period } });
  },

  /**
   * Get paginated wallet trades for the current user on a given chain,
   * with AlphaTrade matching
   */
  getWalletTrades: async (
    chainId: number,
    page: number = 1,
    perPage: number = 25,
    filters: WalletTradeFilters = {}
  ): Promise<WalletTradesResponse> => {
    return api.get<WalletTradesResponse>('/api/alpha_strategies/wallet_trades', {
      params: { chain_id: chainId, page, per_page: perPage, ...filters },
    });
  },

  /**
   * Get FIFO PnL breakdown for a single sell trade
   */
  getSellPnlBreakdown: async (
    chainId: number,
    txHash: string
  ): Promise<FifoSellBreakdown> => {
    return api.get<FifoSellBreakdown>('/api/alpha_strategies/sell_pnl_breakdown', {
      params: { chain_id: chainId, tx_hash: txHash },
    });
  },

  /**
   * Get PnL chart data scoped to a single strategy's buys (FIFO).
   * Only buys from this strategy seed the queue; sells from any source are matched.
   */
  getStrategyPnLChart: async (
    strategyId: number,
    period: string = '7d'
  ): Promise<PnLChartResponse> => {
    return api.get<PnLChartResponse>(`/api/alpha_strategies/${strategyId}/strategy_pnl_chart`, {
      params: { period },
    });
  },

  /**
   * Get paginated wallet trades scoped to a strategy.
   * Buys: only from this strategy. Sells: any that consumed from this strategy's buys via FIFO.
   */
  getStrategyWalletTrades: async (
    strategyId: number,
    page: number = 1,
    perPage: number = 25
  ): Promise<WalletTradesResponse> => {
    return api.get<WalletTradesResponse>(`/api/alpha_strategies/${strategyId}/strategy_wallet_trades`, {
      params: { page, per_page: perPage },
    });
  },

  /**
   * Get paginated buy trades for a strategy with per-trade ATH data.
   */
  getBuyPerformance: async (
    strategyId: number,
    page: number = 1,
    perPage: number = 25,
    params?: { sort_by?: string; sort_direction?: string; sort_window?: PerformanceTimeframe; token_search?: string; buy_period?: PerformanceTimeframe }
  ): Promise<BuyPerformanceResponse> => {
    return api.get<BuyPerformanceResponse>(`/api/alpha_strategies/${strategyId}/buy_performance`, {
      params: { page, per_page: perPage, ...params },
    });
  },

  /**
   * Start async XLSX export of buy performance data.
   */
  exportBuyPerformance: async (strategyId: number): Promise<ExportJobResponse> => {
    return api.post<ExportJobResponse>(`/api/alpha_strategies/${strategyId}/export_buy_performance`);
  },

  /**
   * Poll the status of a buy performance export job.
   */
  getExportStatus: async (strategyId: number, jobId: string): Promise<ExportStatusResponse> => {
    return api.get<ExportStatusResponse>(`/api/alpha_strategies/${strategyId}/export_buy_performance_status`, {
      params: { job_id: jobId },
    });
  },

  /**
   * Enqueue a performance refresh. Pass alphaTradeId to refresh a single trade,
   * omit it to refresh every buy in the strategy. 409 if one is already running.
   */
  refreshPerformance: async (
    strategyId: number,
    alphaTradeId?: number
  ): Promise<PerformanceRefresh> => {
    const body = alphaTradeId ? { alpha_trade_id: alphaTradeId } : {};
    return api.post<PerformanceRefresh>(`/api/alpha_strategies/${strategyId}/refresh_performance`, body);
  },

  /**
   * Get performance summary (scatter-plot points + aggregates) for a given timeframe,
   * along with the current/latest refresh job for progress display.
   */
  getPerformanceSummary: async (
    strategyId: number,
    timeframe: PerformanceTimeframe,
    buyPeriod: PerformanceTimeframe = 'all'
  ): Promise<PerformanceSummaryResponse> => {
    return api.get<PerformanceSummaryResponse>(
      `/api/alpha_strategies/${strategyId}/performance_summary`,
      { params: { timeframe, buy_period: buyPeriod } }
    );
  },

  /**
   * Admin-only: buy-performance aggregates for every strategy of every user.
   * Server responds 403 for non-admin users.
   */
  getAdminPerformanceOverview: async (
    timeframe: PerformanceTimeframe,
    buyPeriod: PerformanceTimeframe = 'all'
  ): Promise<AdminStrategyPerformanceResponse> => {
    return api.get<AdminStrategyPerformanceResponse>('/api/admin/strategy_performance', {
      params: { timeframe, buy_period: buyPeriod },
    });
  },

  /**
   * Poll the status of an AlphaPerformance refresh job.
   */
  getPerformanceRefresh: async (refreshId: number): Promise<PerformanceRefresh> => {
    return api.get<PerformanceRefresh>(`/api/alpha_performance_refreshes/${refreshId}`);
  },

  /**
   * Get wallet address and native chain balance
   */
  getWalletInfo: async (chainId: number): Promise<WalletInfoResponse> => {
    return api.get<WalletInfoResponse>('/api/alpha_strategies/wallet_info', { params: { chain_id: chainId } });
  },

  /**
   * Get active and recently active strategies with 30d trade counts
   */
  getActiveRecentStrategies: async (chainId: number): Promise<ActiveRecentStrategiesResponse> => {
    return api.get<ActiveRecentStrategiesResponse>('/api/alpha_strategies/active_recent', { params: { chain_id: chainId } });
  },

  // ============================================
  // Templates
  // ============================================

  /**
   * Get list of available strategy templates
   * @param chainId - Optional chain ID filter
   */
  getTemplates: async (chainId?: number): Promise<TemplatesListResponse> => {
    const params: Record<string, any> = {};
    if (chainId) params.chain_id = chainId;
    return api.get<TemplatesListResponse>('/api/strategy_templates', { params });
  },

  /**
   * Get strategies created from a specific template.
   */
  getTemplateStrategies: async (
    templateId: string,
    chainId?: number
  ): Promise<TemplateStrategiesResponse> => {
    const params: Record<string, unknown> = {};
    if (chainId) params.chain_id = chainId;
    return api.get<TemplateStrategiesResponse>(
      `/api/strategy_templates/${templateId}/strategies`,
      { params }
    );
  },

  /**
   * Create strategy from template
   * @param templateId - Template ID
   * @param params - Template parameters
   * @param strategyName - Optional strategy name
   */
  createFromTemplate: async (
    templateId: string,
    params: Record<string, unknown>,
    chainId: number,
    strategyName?: string
  ): Promise<CreateFromTemplateResponse> => {
    return api.post<CreateFromTemplateResponse>('/api/strategy_templates', {
      template_id: templateId,
      template: params,
      chain_id: chainId,
      strategy_name: strategyName,
    });
  },

  /**
   * Load edit state for a template-created strategy
   */
  getTemplateEditPayload: async (strategyId: number): Promise<TemplateEditPayload> => {
    return api.get<TemplateEditPayload>(`/api/strategy_templates/strategies/${strategyId}/edit`);
  },

  /**
   * Update an existing template-created strategy
   */
  updateTemplateStrategy: async (
    strategyId: number,
    params: Record<string, unknown>,
    strategyName?: string,
    concurrency?: TemplateEditConcurrency
  ): Promise<UpdateTemplateStrategyResponse> => {
    return api.patch<UpdateTemplateStrategyResponse>(
      `/api/strategy_templates/strategies/${strategyId}`,
      {
        template: params,
        strategy_name: strategyName,
        runtime_revision: concurrency?.runtimeRevision,
        variables: concurrency?.variables.map((variable) => ({
          name: variable.name,
          lock_version: variable.lock_version,
        })),
      }
    );
  },

  // ============================================
  // Backtests
  // ============================================

  /**
   * Get backtest trades for a token
   * @param backtestId - Backtest run ID
   * @param tokenAddress - Token contract address
   * @param start - Optional start date
   * @param end - Optional end date
   */
  getBacktestTrades: async (
    backtestId: number,
    tokenAddress: string,
    start?: string,
    end?: string
  ): Promise<BacktestTradesResponse> => {
    const params: Record<string, any> = { token_address: tokenAddress };
    if (start) params.start = start;
    if (end) params.end = end;
    return api.get<BacktestTradesResponse>(`/api/backtests/${backtestId}/trades`, { params });
  },

  /**
   * Get backtest events for a token
   * @param backtestId - Backtest run ID
   * @param tokenAddress - Token contract address
   * @param start - Optional start date
   * @param end - Optional end date
   */
  getBacktestEvents: async (
    backtestId: number,
    tokenAddress: string,
    start?: string,
    end?: string
  ): Promise<BacktestEventsResponse> => {
    const params: Record<string, any> = { token_address: tokenAddress };
    if (start) params.start = start;
    if (end) params.end = end;
    return api.get<BacktestEventsResponse>(`/api/backtests/${backtestId}/events`, { params });
  },
};
